import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import "../assets/css/Projects.css";
import "../assets/css/Accounts.css";
import "../assets/css/DigixLeads.css";
import IssuesTable from "./issues/IssuesTable";
import { deleteIssue, fetchAllUsers, fetchIssues, fetchIssueStatuses } from "./issues/issueService";
import TestCasesTable from "./issues/TestCasesTable";
import { deleteTestCase, fetchTestCases } from "./issues/testCaseService";
import { fetchAllTasks } from "./taskService";
import { fetchSubTasksByTaskId } from "./subTaskService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";
import { useConfirm } from "../components/ConfirmModal";

const selectStyle = {
  height: 32,
  borderRadius: 5,
  border: "1px solid #D6E0EA",
  padding: "0 10px",
  fontSize: 13,
  background: "#fff",
  minWidth: 140
};
const PRIORITY_FILTER_OPTIONS = ["High", "Medium", "Low"];

function normalizeRoleText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function issueStatusBucket(statusRaw) {
  const status = normalizeText(statusRaw);
  if (!status) return "";
  // Reopened can match "open" substring, so handle it first.
  const compact = status.replace(/[\s_-]+/g, "");
  if (compact.includes("reopen")) return "reopened";
  // Match "In Progress" variants.
  if (compact === "inprogress" || status.includes("in progress") || status.includes("inprogress")) return "inProgress";
  if (status === "open" || status.includes("open")) return "open";
  if (
    status.includes("resolved") ||
    status.includes("closed") ||
    status.includes("complete") ||
    status.includes("done")
  ) return "resolved";
  return "";
}

function parsePossibleIds(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => Number(String(v).trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

function unwrapTestCaseRow(tc) {
  if (!tc || typeof tc !== "object") return tc;
  return tc.test_case || tc.testCase || tc;
}

function getTestCaseAssignedUserIds(tcRaw) {
  const tc = unwrapTestCaseRow(tcRaw);
  const ids = [];
  const assignedUsers = Array.isArray(tc?.Assigned_Users)
    ? tc.Assigned_Users
    : Array.isArray(tc?.assigned_users)
      ? tc.assigned_users
      : [];
  for (const u of assignedUsers) {
    const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
    if (Number.isFinite(uid) && uid > 0) ids.push(uid);
  }
  ids.push(
    ...parsePossibleIds(tc?.Assigned_User_Ids),
    ...parsePossibleIds(tc?.assigned_user_ids),
    ...parsePossibleIds(tc?.Assigned_User_Id),
    ...parsePossibleIds(tc?.assigned_user_id),
    ...parsePossibleIds(tc?.Assigned_To),
    ...parsePossibleIds(tc?.assigned_to),
    ...parsePossibleIds(tc?.Assigned_To_Id),
    ...parsePossibleIds(tc?.assigned_to_id)
  );
  return Array.from(new Set(ids));
}

function isTestCaseAssignedToUser(tcRaw, userId) {
  if (userId == null) return false;
  const ids = getTestCaseAssignedUserIds(tcRaw);
  return ids.includes(Number(userId));
}

function getIssueAssignedUserIds(issue) {
  const ids = [];
  const assignedUsers = Array.isArray(issue?.Assigned_Users)
    ? issue.Assigned_Users
    : Array.isArray(issue?.assigned_users)
      ? issue.assigned_users
      : [];
  for (const u of assignedUsers) {
    const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
    if (Number.isFinite(uid) && uid > 0) ids.push(uid);
  }
  ids.push(
    ...parsePossibleIds(issue?.Assigned_User_Ids),
    ...parsePossibleIds(issue?.assigned_user_ids),
    ...parsePossibleIds(issue?.Assigned_User_Id),
    ...parsePossibleIds(issue?.assigned_user_id),
    ...parsePossibleIds(issue?.Assigned_To),
    ...parsePossibleIds(issue?.assigned_to),
    ...parsePossibleIds(issue?.Assigned_To_Id),
    ...parsePossibleIds(issue?.assigned_to_id)
  );
  return Array.from(new Set(ids));
}

function isIssueAssignedToUser(issue, userId) {
  if (userId == null) return false;
  return getIssueAssignedUserIds(issue).includes(Number(userId));
}

function Issues() {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);

  const canCreate = canPerformActionOnPage(PAGES.ISSUES, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.ISSUES, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.ISSUES, ACTIONS.DELETE, role);
  const isDeveloperRole = useMemo(() => normalizeRoleText(role).includes("developer"), [role]);
  const isDevopsRole = useMemo(() => normalizeRoleText(role).includes("devops"), [role]);
  const isTesterRole = useMemo(() => normalizeRoleText(role).includes("tester"), [role]);
  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(getAuthItem("user") || "{}");
      const n = Number(u.user_id ?? u.User_Id ?? u.id);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, []);
  const currentUserName = useMemo(() => {
    try {
      const u = JSON.parse(getAuthItem("user") || "{}");
      return String(u.user_name ?? u.User_Name ?? u.name ?? "").trim();
    } catch {
      return "";
    }
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.startsWith("/issues/test-cases")) return "Test Cases";
    return "Issues";
  });
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    taskId: "",
    titleQuery: "",
    assignedUserId: (isDeveloperRole || isDevopsRole) && currentUserId != null ? String(currentUserId) : "",
    status: "",
    priority: ""
  });

  const [testCases, setTestCases] = useState([]);
  const [tcLoading, setTcLoading] = useState(false);
  const [tcError, setTcError] = useState("");
  const [tcFilters, setTcFilters] = useState({
    taskId: "",
    titleQuery: "",
    testCaseId: "",
    status: "",
    priority: ""
  });

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [issueStatuses, setIssueStatuses] = useState([]);
  const [issueFreeSearch, setIssueFreeSearch] = useState("");
  const [dashboardIssueStatusPreset, setDashboardIssueStatusPreset] = useState("");
  const [testCaseFreeSearch, setTestCaseFreeSearch] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const taskDropdownRef = useRef(null);
  const [assignedUserDropdownOpen, setAssignedUserDropdownOpen] = useState(false);
  const [assignedUserSearchQuery, setAssignedUserSearchQuery] = useState("");
  const assignedUserDropdownRef = useRef(null);
  const [tcTaskDropdownOpen, setTcTaskDropdownOpen] = useState(false);
  const [tcTaskSearchQuery, setTcTaskSearchQuery] = useState("");
  const tcTaskDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(e.target)) { setTaskDropdownOpen(false); setTaskSearchQuery(""); }
      if (assignedUserDropdownRef.current && !assignedUserDropdownRef.current.contains(e.target)) { setAssignedUserDropdownOpen(false); setAssignedUserSearchQuery(""); }
      if (tcTaskDropdownRef.current && !tcTaskDropdownRef.current.contains(e.target)) { setTcTaskDropdownOpen(false); setTcTaskSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoadingOptions(true);
      try {
        // Omit fetchTestCases here — it is heavy and only needed on the Test Cases tab; load lazily below.
        const [taskList, userList, statusList] = await Promise.all([
          fetchAllTasks(),
          fetchAllUsers(),
          fetchIssueStatuses()
        ]);
        const mappedTasks = (taskList || [])
          .map((t) => ({ id: t.Task_Id ?? t.task_id ?? t.id, title: t.Title ?? t.title ?? t.Task_Title ?? t.task_title ?? "—" }))
          .filter((x) => x.id != null);
        mappedTasks.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTasks(mappedTasks);

        setUsers((userList || []).sort((a, b) => String(a.name).localeCompare(String(b.name))));

        const mappedStatuses = (statusList || [])
          .map((s) => String(s?.name || "").trim())
          .filter(Boolean);
        const uniqueStatuses = Array.from(new Set(mappedStatuses)).sort((a, b) => a.localeCompare(b));
        setIssueStatuses(uniqueStatuses);
      } catch (err) {
        console.error("Error loading filter options:", err);
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/issues/test-cases")) setActiveTab("Test Cases");
    else setActiveTab("Issues");
  }, [location.pathname]);

  useEffect(() => {
    const preset = location.state?.dashboardIssueStatusPreset;
    if (typeof preset !== "string" || !preset) return;
    setActiveTab("Issues");
    setDashboardIssueStatusPreset(preset);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!dashboardIssueStatusPreset) return;
    const matchedStatus =
      (issueStatuses || []).find((s) => issueStatusBucket(s) === dashboardIssueStatusPreset) ||
      (dashboardIssueStatusPreset === "open"
        ? "Open"
        : dashboardIssueStatusPreset === "resolved"
          ? "Resolved"
          : dashboardIssueStatusPreset === "inProgress"
            ? "In Progress"
            : dashboardIssueStatusPreset === "reopened"
              ? "Reopened"
              : "");
    if (!matchedStatus) return;
    setFilters((prev) => ({ ...prev, status: matchedStatus }));
  }, [dashboardIssueStatusPreset, issueStatuses]);

  const dedupeIssuesById = (list) => {
    const arr = Array.isArray(list) ? list : [];
    const map = new Map();
    const noIdRows = [];
    for (const item of arr) {
      const id = item?.Issue_Id ?? item?.issue_id ?? item?.id;
      if (id == null) {
        noIdRows.push(item);
        continue;
      }
      if (!map.has(String(id))) {
        map.set(String(id), item);
      }
    }
    return [...map.values(), ...noIdRows];
  };

  const applyFilters = async (nextFilters) => {
    setLoading(true);
    setError("");
    try {
      const roleRestricted = (isDeveloperRole || isDevopsRole) && currentUserId != null;
      const queryFilters = roleRestricted
        ? {
            taskId: nextFilters?.taskId,
            testCaseId: nextFilters?.testCaseId,
            status: nextFilters?.status
          }
        : nextFilters;
      let list = await fetchIssues(queryFilters);
      list = Array.isArray(list) ? list : [];
      if (queryFilters?.status) {
        const wanted = String(queryFilters.status).trim().toLowerCase();
        list = list.filter((issue) => String(issue?.Status ?? issue?.status ?? "").trim().toLowerCase() === wanted);
      }
      if (queryFilters?.priority) {
        const wantedPriority = String(queryFilters.priority).trim().toLowerCase();
        list = list.filter((issue) => String(issue?.Priority ?? issue?.priority ?? "").trim().toLowerCase() === wantedPriority);
      }
      if (queryFilters?.titleQuery && String(queryFilters.titleQuery).trim()) {
        const q = String(queryFilters.titleQuery).trim().toLowerCase();
        list = list.filter((issue) =>
          String(issue?.Title ?? issue?.title ?? "")
            .toLowerCase()
            .includes(q)
        );
      }
      if (roleRestricted) {
        const me = normalizeText(currentUserName);
        list = list.filter((issue) => {
          if (isIssueAssignedToUser(issue, currentUserId)) return true;
          const createdById = Number(issue?.Created_By ?? issue?.created_by);
          if (Number.isFinite(createdById) && createdById === Number(currentUserId)) return true;
          const createdByName = normalizeText(issue?.Created_By_Name ?? issue?.created_by_name);
          return me && createdByName === me;
        });
      }
      if (isTesterRole) {
        const me = normalizeText(currentUserName);
        list = list.filter((issue) => {
          const createdById = Number(issue?.Created_By ?? issue?.created_by);
          if (currentUserId != null && Number.isFinite(createdById) && createdById === Number(currentUserId)) return true;
          const createdByName = normalizeText(issue?.Created_By_Name ?? issue?.created_by_name);
          return me && createdByName === me;
        });
      }
      setIssues(dedupeIssuesById(list));
    } catch (err) {
      console.error("Error fetching issues:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to load issues.");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  const applyTestCaseFilters = async (nextFilters) => {
    setTcLoading(true);
    setTcError("");
    try {
      let list = await fetchTestCases({
        taskId: nextFilters.taskId
      });
      list = Array.isArray(list) ? list : [];

      if (nextFilters.testCaseId && Number.isFinite(Number(nextFilters.testCaseId))) {
        const id = Number(nextFilters.testCaseId);
        list = list.filter((tc) => (tc.Test_Case_Id ?? tc.test_case_id ?? tc.id) === id);
      }
      if (nextFilters.status) {
        list = list.filter((tc) => (tc.Status ?? tc.status ?? "") === nextFilters.status);
      }
      if (nextFilters.priority) {
        const wantedPriority = String(nextFilters.priority).trim().toLowerCase();
        list = list.filter((tc) => String(tc.Priority ?? tc.priority ?? "").trim().toLowerCase() === wantedPriority);
      }
      if (nextFilters.titleQuery && String(nextFilters.titleQuery).trim()) {
        const q = String(nextFilters.titleQuery).trim().toLowerCase();
        list = list.filter((tc) =>
          String(tc.Title ?? tc.title ?? "")
            .toLowerCase()
            .includes(q)
        );
      }
      if ((isDeveloperRole || isDevopsRole) && currentUserId != null) {
        list = list.filter((tc) => isTestCaseAssignedToUser(tc, currentUserId));
      }
      setTestCases(list);
    } catch (err) {
      console.error("Error fetching test cases:", err);
      setTcError(err.response?.data?.message || err.response?.data?.error || "Failed to load test cases.");
      setTestCases([]);
    } finally {
      setTcLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith("/issues/test-cases")) {
      applyTestCaseFilters(tcFilters);
    } else {
      applyFilters(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== "Issues") return;
    applyFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  useEffect(() => {
    if (activeTab !== "Test Cases") return;
    applyTestCaseFilters(tcFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tcFilters]);

  useEffect(() => {
    if ((!isDeveloperRole && !isDevopsRole) || currentUserId == null) return;
    const idStr = String(currentUserId);
    setFilters((prev) => (prev.assignedUserId === idStr ? prev : { ...prev, assignedUserId: idStr }));
  }, [isDeveloperRole, isDevopsRole, currentUserId]);

  const visibleIssues = useMemo(() => {
    const q = String(issueFreeSearch || "").trim().toLowerCase();
    const base = (issues || []).filter((i) => {
      if (!dashboardIssueStatusPreset) return true;
      return issueStatusBucket(i?.Status ?? i?.status ?? "") === dashboardIssueStatusPreset;
    });
    if (!q) return base;
    return base.filter((i) => {
      const assignedUsers = Array.isArray(i?.Assigned_Users)
        ? i.Assigned_Users
        : Array.isArray(i?.assigned_users)
          ? i.assigned_users
          : [];
      const assignedText = assignedUsers
        .map((u) => u?.User_Name ?? u?.user_name ?? u?.name ?? "")
        .filter(Boolean)
        .join(", ")
        .toLowerCase();
      const bag = [
        i?.Title ?? i?.title ?? "",
        i?.Type ?? i?.type ?? "",
        i?.Issue_Layer ?? i?.issue_layer ?? "",
        i?.Priority ?? i?.priority ?? "",
        i?.Status ?? i?.status ?? "",
        i?.Task_Title ?? i?.task_title ?? i?.Task_Id ?? i?.task_id ?? "",
        i?.Sub_Task_Title ?? i?.sub_task_title ?? i?.Sub_Task_Id ?? i?.sub_task_id ?? "",
        i?.Created_By_Name ?? i?.created_by_name ?? "",
        assignedText
      ]
        .map((v) => String(v || "").toLowerCase());
      return bag.some((v) => v.includes(q));
    });
  }, [issues, issueFreeSearch, dashboardIssueStatusPreset]);

  const visibleTestCases = useMemo(() => {
    const q = String(testCaseFreeSearch || "").trim().toLowerCase();
    if (!q) return testCases;
    return (testCases || []).filter((tc) => {
      const bag = [
        tc?.Title ?? tc?.title ?? "",
        tc?.Description ?? tc?.description ?? "",
        tc?.Status ?? tc?.status ?? "",
        tc?.Priority ?? tc?.priority ?? "",
        tc?.Task_Title ?? tc?.task_title ?? tc?.Task_Id ?? tc?.task_id ?? "",
        tc?.Sub_Task_Title ?? tc?.sub_task_title ?? tc?.Sub_Task_Id ?? tc?.sub_task_id ?? "",
        tc?.Created_By_Name ?? tc?.created_by_name ?? "",
        tc?.Comments ?? tc?.comments ?? ""
      ]
        .map((v) => String(v || "").toLowerCase());
      return bag.some((v) => v.includes(q));
    });
  }, [testCases, testCaseFreeSearch]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "Test Cases") {
      navigate("/issues/test-cases", { replace: true });
      applyTestCaseFilters(tcFilters);
    } else {
      navigate("/issues", { replace: true });
      applyFilters(filters);
    }
  };

  return (
    <div className="projects-container">
      <div className="main-card">
        <div className="page-header" style={{ marginBottom: 12 }}>
          <div className="tabs-container">
            <button
              className={`tab-button ${activeTab === "Issues" ? "active" : ""}`}
              onClick={() => handleTabChange("Issues")}
            >
              Issues
            </button>
            <button
              className={`tab-button ${activeTab === "Test Cases" ? "active" : ""}`}
              onClick={() => handleTabChange("Test Cases")}
            >
              Test Cases
            </button>
          </div>
        </div>

        {activeTab === "Issues" ? (
        <>
        <div
          className="projects-filters-row"
          style={{
            marginBottom: 12,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center"
          }}
        >
          <input
            className="search-filter-input"
            style={{ ...selectStyle, width: 180 }}
            type="text"
            placeholder="Search title..."
            value={filters.titleQuery}
            onChange={(e) => setFilters((p) => ({ ...p, titleQuery: e.target.value }))}
          />
          <div className="domain-filter-dropdown" ref={taskDropdownRef} style={{ minWidth: 180 }}>
            <div
              className="search-filter-input search-filter-select domain-filter-trigger"
              style={{
                ...selectStyle,
                width: 180,
                opacity: loadingOptions ? 0.6 : 1,
                cursor: loadingOptions ? "default" : "pointer"
              }}
              onClick={() => !loadingOptions && setTaskDropdownOpen((o) => !o)}
              role="button"
              tabIndex={loadingOptions ? -1 : 0}
              onKeyDown={(e) => {
                if (!loadingOptions && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setTaskDropdownOpen((o) => !o);
                }
              }}
            >
              <span>{filters.taskId ? (tasks.find((t) => String(t.id) === filters.taskId)?.title || "All Tasks") : "All Tasks"}</span>
              <FaChevronDown className={`domain-filter-chevron ${taskDropdownOpen ? "open" : ""}`} />
            </div>
            {taskDropdownOpen && !loadingOptions && (
              <div className="domain-filter-panel">
                <input
                  type="text"
                  className="domain-filter-search"
                  placeholder="Search tasks..."
                  value={taskSearchQuery}
                  onChange={(e) => setTaskSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="domain-filter-options">
                  <div
                    className={`domain-filter-option ${!filters.taskId ? "selected" : ""}`}
                    onClick={() => {
                      setFilters((p) => ({ ...p, taskId: "", subTaskId: "" }));
                      setTaskDropdownOpen(false);
                      setTaskSearchQuery("");
                    }}
                  >
                    All Tasks
                  </div>
                  {tasks
                    .filter(
                      (t) =>
                        !taskSearchQuery.trim() ||
                        String(t.title || "").toLowerCase().includes(taskSearchQuery.trim().toLowerCase())
                    )
                    .map((t) => (
                      <div
                        key={t.id}
                        className={`domain-filter-option ${filters.taskId === String(t.id) ? "selected" : ""}`}
                        onClick={() => {
                          setFilters((p) => ({ ...p, taskId: String(t.id), subTaskId: "" }));
                          setTaskDropdownOpen(false);
                          setTaskSearchQuery("");
                        }}
                      >
                        {t.title}
                      </div>
                    ))}
                  {tasks.filter((t) => !taskSearchQuery.trim() || String(t.title || "").toLowerCase().includes(taskSearchQuery.trim().toLowerCase())).length === 0 && (
                    <div className="domain-filter-no-results">No tasks found</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="domain-filter-dropdown" ref={assignedUserDropdownRef} style={{ minWidth: 180 }}>
            <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ ...selectStyle, width: 180, opacity: loadingOptions || isDeveloperRole || isDevopsRole ? 0.6 : 1, cursor: loadingOptions || isDeveloperRole || isDevopsRole ? "default" : "pointer" }} onClick={() => !loadingOptions && !isDeveloperRole && !isDevopsRole && setAssignedUserDropdownOpen((o) => !o)} role="button" tabIndex={loadingOptions || isDeveloperRole || isDevopsRole ? -1 : 0} onKeyDown={(e) => { if (!loadingOptions && !isDeveloperRole && !isDevopsRole && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setAssignedUserDropdownOpen((o) => !o); } }}>
              <span>{filters.assignedUserId ? (users.find((u) => String(u.id) === filters.assignedUserId)?.name || "All Assigned Users") : "All Assigned Users"}</span>
              <FaChevronDown className={`domain-filter-chevron ${assignedUserDropdownOpen ? "open" : ""}`} />
            </div>
            {assignedUserDropdownOpen && !loadingOptions && !isDeveloperRole && !isDevopsRole && (
              <div className="domain-filter-panel">
                <input type="text" className="domain-filter-search" placeholder="Search users..." value={assignedUserSearchQuery} onChange={(e) => setAssignedUserSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                <div className="domain-filter-options">
                  <div className={`domain-filter-option ${!filters.assignedUserId ? "selected" : ""}`} onClick={() => { setFilters((p) => ({ ...p, assignedUserId: "" })); setAssignedUserDropdownOpen(false); setAssignedUserSearchQuery(""); }}>All Assigned Users</div>
                  {users.filter((u) => !assignedUserSearchQuery.trim() || String(u.name || "").toLowerCase().includes(assignedUserSearchQuery.trim().toLowerCase())).map((u) => (
                    <div key={u.id} className={`domain-filter-option ${filters.assignedUserId === String(u.id) ? "selected" : ""}`} onClick={() => { setFilters((p) => ({ ...p, assignedUserId: String(u.id) })); setAssignedUserDropdownOpen(false); setAssignedUserSearchQuery(""); }}>{u.name}</div>
                  ))}
                  {users.filter((u) => !assignedUserSearchQuery.trim() || String(u.name || "").toLowerCase().includes(assignedUserSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No users found</div>}
                </div>
              </div>
            )}
          </div>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters((p) => ({ ...p, status: e.target.value }));
            }}
            style={{ ...selectStyle, width: 160 }}
          >
            <option value="">All Status</option>
            {issueStatuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={filters.priority}
            onChange={(e) => {
              setFilters((p) => ({ ...p, priority: e.target.value }));
            }}
            style={{ ...selectStyle, width: 150 }}
          >
            <option value="">All Priority</option>
            {PRIORITY_FILTER_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              const emptyFilters = {
                taskId: "",
                titleQuery: "",
                assignedUserId: (isDeveloperRole || isDevopsRole) && currentUserId != null ? String(currentUserId) : "",
                status: "",
                priority: ""
              };
              setDashboardIssueStatusPreset("");
              setFilters(emptyFilters);
              setTaskDropdownOpen(false);
              setAssignedUserDropdownOpen(false);
              setTaskSearchQuery("");
              setAssignedUserSearchQuery("");
            }}
            style={{ marginLeft: 8 }}
          >
            Clear
          </button>
          <div style={{ marginLeft: "auto" }} />
          {canCreate ? (
            <button
              type="button"
              className="btn-add-lead"
              onClick={() => navigate("/issues/add")}
            >
              Add Issue
            </button>
          ) : null}
        </div>
        <div className="search-row-2">
          <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
            <input
              type="text"
              className="free-search-input"
              
              value={issueFreeSearch}
              onChange={(e) => setIssueFreeSearch(e.target.value)}
                placeholder="Search..."
              title="Search across issue table columns"
            />
          </div>
        </div>
        </>
        ) : (
          <>
          <div
            className="projects-filters-row"
            style={{
              marginBottom: 12,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center"
            }}
          >
            <input
              className="search-filter-input"
              style={{ ...selectStyle, width: 180 }}
              type="text"
              placeholder="Search title..."
              value={tcFilters.titleQuery}
              onChange={(e) => setTcFilters((p) => ({ ...p, titleQuery: e.target.value }))}
            />
            <div className="domain-filter-dropdown" ref={tcTaskDropdownRef} style={{ minWidth: 180 }}>
              <div
                className="search-filter-input search-filter-select domain-filter-trigger"
                style={{
                  ...selectStyle,
                  width: 180,
                  opacity: loadingOptions ? 0.6 : 1,
                  cursor: loadingOptions ? "default" : "pointer"
                }}
                onClick={() => !loadingOptions && setTcTaskDropdownOpen((o) => !o)}
                role="button"
                tabIndex={loadingOptions ? -1 : 0}
                onKeyDown={(e) => {
                  if (!loadingOptions && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    setTcTaskDropdownOpen((o) => !o);
                  }
                }}
              >
                <span>{tcFilters.taskId ? (tasks.find((t) => String(t.id) === tcFilters.taskId)?.title || "All Tasks") : "All Tasks"}</span>
                <FaChevronDown className={`domain-filter-chevron ${tcTaskDropdownOpen ? "open" : ""}`} />
              </div>
              {tcTaskDropdownOpen && !loadingOptions && (
                <div className="domain-filter-panel">
                  <input
                    type="text"
                    className="domain-filter-search"
                    placeholder="Search tasks..."
                    value={tcTaskSearchQuery}
                    onChange={(e) => setTcTaskSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <div className="domain-filter-options">
                    <div
                      className={`domain-filter-option ${!tcFilters.taskId ? "selected" : ""}`}
                      onClick={() => {
                        setTcFilters((p) => ({ ...p, taskId: "", testCaseId: "" }));
                        setTcTaskDropdownOpen(false);
                        setTcTaskSearchQuery("");
                      }}
                    >
                      All Tasks
                    </div>
                    {tasks
                      .filter(
                        (t) =>
                          !tcTaskSearchQuery.trim() ||
                          String(t.title || "").toLowerCase().includes(tcTaskSearchQuery.trim().toLowerCase())
                      )
                      .map((t) => (
                        <div
                          key={t.id}
                          className={`domain-filter-option ${tcFilters.taskId === String(t.id) ? "selected" : ""}`}
                          onClick={() => {
                            setTcFilters((p) => ({ ...p, taskId: String(t.id), testCaseId: "" }));
                            setTcTaskDropdownOpen(false);
                            setTcTaskSearchQuery("");
                          }}
                        >
                          {t.title}
                        </div>
                      ))}
                    {tasks.filter((t) => !tcTaskSearchQuery.trim() || String(t.title || "").toLowerCase().includes(tcTaskSearchQuery.trim().toLowerCase())).length === 0 && (
                      <div className="domain-filter-no-results">No tasks found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <select
              value={tcFilters.status}
              onChange={(e) => setTcFilters((p) => ({ ...p, status: e.target.value }))}
              style={{ ...selectStyle, width: 160 }}
            >
              <option value="">All Status</option>
              <option value="Not Executed">Not Executed</option>
              <option value="Passed">Passed</option>
              <option value="Failed">Failed</option>
              <option value="Blocked">Blocked</option>
            </select>
            <select
              value={tcFilters.priority}
              onChange={(e) => setTcFilters((p) => ({ ...p, priority: e.target.value }))}
              style={{ ...selectStyle, width: 150 }}
            >
              <option value="">All Priority</option>
              {PRIORITY_FILTER_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="btn-clear"
              onClick={() => {
                const emptyTcFilters = { taskId: "", titleQuery: "", testCaseId: "", status: "", priority: "" };
                setTcFilters(emptyTcFilters);
                setTcTaskDropdownOpen(false);
                setTcTaskSearchQuery("");
              }}
              style={{ marginLeft: 8 }}
            >
              Clear
            </button>
            <div style={{ marginLeft: "auto" }} />
            {canCreate ? (
              <button
                type="button"
                className="btn-add-lead"
                onClick={() => navigate("/issues/test-cases/add")}
              >
                Add Test Case
              </button>
            ) : null}
          </div>
          <div className="search-row-2">
            <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
              <input
                type="text"
                className="free-search-input"
                placeholder="Search..."
                value={testCaseFreeSearch}
                onChange={(e) => setTestCaseFreeSearch(e.target.value)}
                title="Search across test case table columns"
              />
            </div>
          </div>
          </>
        )}

        {activeTab === "Issues" ? (
          loading ? (
          <div style={{ padding: 18, color: "#5E6C84" }}>Loading issues...</div>
        ) : error ? (
          <div style={{ padding: 18, color: "#c53030" }}>{error}</div>
        ) : (
          <IssuesTable
            issues={visibleIssues}
            onViewIssue={(id) => navigate(`/issues/${id}/view`)}
            onEditIssue={canEdit ? ((id) => navigate(`/issues/${id}/edit`)) : undefined}
            onDeleteIssue={canDelete ? (async (id) => {
              const ok = await confirm({ title: "Delete Issue", message: "Are you sure you want to delete this issue?" });
              if (!ok) return;
              try {
                await deleteIssue(id);
                alert("Issue deleted successfully.");
                applyFilters(filters);
              } catch (err) {
                console.error("Error deleting issue:", err);
                alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete issue.");
              }
            }) : undefined}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )
        ) : (
          tcLoading ? (
            <div style={{ padding: 18, color: "#5E6C84" }}>Loading test cases...</div>
          ) : tcError ? (
            <div style={{ padding: 18, color: "#c53030" }}>{tcError}</div>
          ) : (
            <TestCasesTable
              testCases={visibleTestCases}
              onViewTestCase={(id) => navigate(`/issues/test-cases/${id}/view`)}
              onEditTestCase={canEdit ? ((id) => navigate(`/issues/test-cases/${id}/edit`)) : undefined}
              onDeleteTestCase={canDelete ? (async (id) => {
                const ok = await confirm({ title: "Delete Test Case", message: "Are you sure you want to delete this test case?" });
                if (!ok) return;
                try {
                  await deleteTestCase(id);
                  alert("Test case deleted successfully.");
                  applyTestCaseFilters(tcFilters);
                } catch (err) {
                  console.error("Error deleting test case:", err);
                  alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete test case.");
                }
              }) : undefined}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          )
        )}
      </div>
    </div>
  );
}

export default Issues;
